<template>
  <section class="product-panel">
    <modal id="quick-preview-article" v-if="quickArticle">
      <div class="modal-body" slot="body">
        <quick-preview :key="quickArticle.id" />
      </div>
    </modal>
    <modal id="addCartModalNewArticles" v-if="selectedArticle">
      <div slot="body" class="modal-body add-cart-box text-center">
        <p class="html-content" v-html="translations.addToCartSuccessMessage"></p>
        <h4 id="productTitle">
          {{ selectedArticle.title }}
        </h4>
        <img
          :src="`${baseUrl}${selectedArticle.highlightedImageUrl}`"
          id="productImage"
          width="100"
          height="100"
          alt="adding cart image"
        />
        <div class="btn-actions">
          <router-link :to="{ name: 'ShoppingCart' }" data-dismiss="modal">
            <button class="btn-primary">
              {{ translations.goToShoppingCart }}
            </button>
          </router-link>
          <button class="btn-primary" data-dismiss="modal">
            {{ translations.continueShopping }}
          </button>
        </div>
      </div>
    </modal>
    <div class="section-title">
      <h2>{{ translations.newArticles }}</h2>
    </div>
    <div>
      <VueSlickCarousel v-bind="slickSettings" v-if="articles && articles.length > 0">
        <div
          class="product-wrapper product-default inner-quickview inner-icon product-item"
          v-for="item in articles"
          :key="item.id"
        >
          <div class="product-inner-wrapper">
            <figure>
              <router-link
                :to="{ name: 'Article', params: { id: item.id, slug: slugify(item.title) } }"
              >
                <Photo
                  :src="`${baseUrl}${item.highlightedImageUrl}`"
                  v-if="item.highlightedImageUrl"
                />
                <Photo src="assets/images/no-image.jpg" v-else />
              </router-link>
              <div class="label-group" v-if="item.salePrice < item.price && item.salePrice">
                <span class="product-label label-cut">
                  {{ item.saleDiscountPercentage | percentage }}%
                </span>
              </div>
              <div class="btn-icon-group" v-if="!item.disableSelling">
                <button class="btn-icon btn-add-cart" @click="addToCart(item)">
                  <i class="sicon-equalizer" v-if="item.hasAttributes" />
                  <i class="icon-bag" v-else />
                </button>
              </div>
              <button
                class="btn btn-sm btn-quickview"
                data-toggle="modal"
                @click="getQuickArticle(item.id)"
              >
                {{ translations.quickPreview }}
              </button>
            </figure>
            <div class="product-details">
              <div class="category-wrap">
                <div class="category-list">
                  <router-link
                    v-if="item.brandId"
                    class="product-category m-0"
                    :to="{ name: 'Brand', params:
                    { id: item.brandId, title: item.brand.replace(/\s+/g, '-') } }"
                  >
                    {{ item.brand }}
                  </router-link>
                  <span v-else class="product-category m-0">
                    {{ item.brand }}
                  </span>
                </div>
                <a
                  class="btn-icon-wish"
                  :class="{ active: item.isOnWishList }"
                  @click.prevent.stop="toggleWishlistItem(item.id)"
                >
                  <i class="icon-heart" :class="{ 'icon-heart-1': item.isOnWishList }"></i>
                </a>
              </div>
              <h2 class="product-title" :title="item.title">
                <router-link
                  :to="{ name: 'Article', params: { id: item.id, slug: slugify(item.title) } }"
                >
                  {{ item.title }}
                </router-link>
              </h2>
              <div class="ratings-container">
                {{ translations.forms.labels.code }}: {{ item.code }}</div>
              <div class="price-box" v-if="(canViewPrices && !item.disableSelling) ||
                canPassRestrictions">
                <span class="product-price">
                  {{ (item.salePrice || item.price) | money3WithCurrency }}</span>
                <span class="old-price" v-if="item.salePrice < item.price && item.salePrice">
                  {{ item.price | money3 }} KM
                </span>
              </div>
            </div>
          </div>
        </div>
      </VueSlickCarousel>
    </div>
    <div class="row row-sm flex-row justify-content-end px-4 font-weight-bold">
      <router-link :to="{ name: 'NewArticles' }">
        {{ translations.viewAll }}
      </router-link>
    </div>
  </section>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel';
import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import './slick.css';

import config from '@/config';
import Photo from '@/components/shared/Image.vue';
import api from '@/services/api';
import slugify from '@/filters/slugify';
import helpers from '@/services/helpers';

import Modal from '@/components/shared/modal/Modal.vue';
import QuickPreview from '@/components/shared/article/QuickPreview.vue';

import { translations } from '@/mixins';

export default {
  name: 'NewArticles',
  mixins: [translations],
  components: {
    Photo,
    VueSlickCarousel,
    Modal,
    QuickPreview,
  },
  data() {
    return {
      slickSettings: {
        dots: false,
        infinite: true,
        slidesToShow: 5,
        slidesToScroll: 1,
        swipeToSlide: true,
        lazyLoad: 'ondemand',
        responsive: [
          {
            breakpoint: 1200,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 3,
              infinite: true,
            },
          },
          {
            breakpoint: 767,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 2,
            },
          },
          {
            breakpoint: 576,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 2,
            },
          },
        ],
      },
      selectedArticle: null,
    };
  },
  computed: {
    articles() {
      return this.$store.getters.getNewlyAddedArticles;
    },
    baseUrl() {
      return config.baseUrl;
    },
    quickArticle() {
      return this.$store.getters.getQuickArticle;
    },
    allowOrderingStockZero() {
      return this.$store.getters.getAllowOrderingStockZero;
    },
    profile() {
      return this.$store.getters.getProfile;
    },
    canViewPrices() {
      return this.profile.userData.canViewPrices;
    },
    canPassRestrictions() {
      return this.profile?.userData?.articleAlwaysVisibleOnWeb;
    },
  },
  mounted() {
    this.$store.dispatch('getNewlyAddedArticles', {
      page: 1,
      pageSize: 10,
      brandIds: [],
      categoryIds: [],
      attributeValuesIds: [],
      tagIds: [],
    });
  },
  methods: {
    slugify,
    toggleWishlistItem(articleId) {
      api.toggleWishlistItem(articleId).then((response) => {
        this.$toast.info(response.data.info);
        // this.$store.dispatch('getNewlyAddedArticles');
        this.$store.dispatch('getWishList');
      });
    },
    getQuickArticle(articleId) {
      this.$store.dispatch('getQuickViewArticle', articleId).then(() => {
        this.$store.dispatch('getArticleVariations', articleId).then(() => {
          this.$store.dispatch('getAttributes', { retrieveAll: true });
          $('#quick-preview-article').modal('show');
        });
      });
    },
    addToCart(item) {
      if (item.hasAttributes || (!item.stockAmount && !this.allowOrderingStockZero)) {
        this.$router.push({ name: 'Article', params: { id: item.id, slug: slugify(item.title) } });
        return;
      }
      this.selectedArticle = item;
      api
        .addToShoppingCart({
          articleId: item.id,
          quantity: item.minOrderQuantity || 1,
        })
        .then(() => {
          this.$toast.info(helpers.getAddToCartToastMessage(item));
          $('#addCartModalNewArticles').modal('show');
          this.$store.dispatch('getShoppingCart');
        }).catch((err) => {
          this.$toast.error(err.response.data.info, { timeout: 4000 });
          this.$store.dispatch('getShoppingCart');
          $('#addCartModalNewArticles').modal('hide');
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.product-wrapper {
  padding: 0 10px;
  border-right: 1px solid #e7e7e7;
  &:last-child {
    border-left: none;
  }
}

.product-inner-wrapper:hover {
  box-shadow: 0 12px 20px 0 rgb(0 0 0 / 8%);
  transition: box-shadow 0.3s ease-in-out;
}

.product-wrapper:hover {
  box-shadow: none;
}

.product-default .product-title,
.product-default a {
  display: block;
  width: 100%;
}

.btn-icon-wish {
  cursor: pointer;

  &.active {
    color: red;
  }
}

.product-item {
  .product-details {
    min-height: 135px !important;
    justify-content: flex-start;
  }
}

.html-content a {
  color: #3598DB;
}
</style>
